import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'router-v6'
import { getDefaultRoute } from 'v2/configs/routes'
import { checkFlowUnsavedChangesForNavigation } from 'v2/pages/flow/utils'
import type { Workspace } from 'v2/shared/navigation/workspace-nav'
import { getWorkspace } from 'v2/shared/navigation/workspace-nav-utils'

export const WORKSPACE_STORAGE_KEY = 'workspace-nav'

// Read synchronously so a shared page shows the remembered workspace on its first render.
// getWorkspace drops a value this session can't open.
const readRemembered = () => {
  try {
    return (localStorage.getItem(WORKSPACE_STORAGE_KEY) ?? undefined) as Workspace | undefined
  } catch {
    return undefined
  }
}

const writeRemembered = (workspace: Workspace) => {
  try {
    localStorage.setItem(WORKSPACE_STORAGE_KEY, workspace)
  } catch {
    // Storage can be unavailable; the page still decides the workspace.
  }
}

/**
 * The workspace the sidebar shows. A page owned by one workspace selects it; a shared page
 * keeps the current one. Switching opens the user's default page.
 */
export const useWorkspace = (available: Workspace[]) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [remembered, setRemembered] = useState(readRemembered)
  const current = getWorkspace(pathname, remembered, available)

  // Runs only when the derived workspace changes, so a pick isn't undone while its page loads.
  useEffect(() => {
    if (!current) return
    setRemembered(current)
    writeRemembered(current)
  }, [current])

  const selectWorkspace = (workspace: Workspace) => {
    if (workspace === current) return
    // A menu item is not a link, so the builder's link guard misses it; ask the builder directly.
    checkFlowUnsavedChangesForNavigation(true, () => {
      setRemembered(workspace)
      writeRemembered(workspace)
      const target = getDefaultRoute()
      if (target !== pathname) navigate(target)
    })
  }

  return { current, selectWorkspace }
}
